import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { FeeDetails } from '../config/feeConfig/feeConfig';
import conversionService from '../services/currencyConversion';
import loadFeeConfig, { getCurrencyCode } from '../config/feeConfig';
import { ChainType } from './GatekeeperNetworkService/GatekeeperNetworkServiceData';
import { getWalletBalanceDetails, WalletBalanceDetails } from '../utils/walletBalance';
import { getWalletBalance } from '../services/walletBalance/walletBalanceService';
import { FlowConfigContext } from './FlowConfigContext';
import { GatekeeperNetworkServiceContext } from './GatekeeperNetworkService/GatekeeperNetworkServiceContext';
import Log from '../common/logging';

export type WalletBalance = { status: 'loading' | 'complete' | 'error'; balance?: string; error?: Error };
export type FeesOutput = {
  fees: FeeDetails | null;
  walletCryptoBalance: WalletBalance | null;
  walletBalanceDetail: WalletBalanceDetails | null;
  cryptoCurrencyCode?: string;
};
export const FeesContext = createContext<FeesOutput>({} as FeesOutput);

export function FeesProvider({ children }: { children: React.ReactNode }) {
  const { initialized, chainName, chainNetwork, action, ownerSignsEnabled, wallet } = useContext(FlowConfigContext);
  const { gknServiceData, requestComplete } = useContext(GatekeeperNetworkServiceContext);

  const [fees, setFees] = useState<FeeDetails | null>(null);
  const [walletCryptoBalance, setWalletCryptoBalance] = useState<WalletBalance | null>(null);
  const [walletBalanceDetail, setWalletBalanceDetail] = useState<WalletBalanceDetails | null>(null);
  const [cryptoCurrencyCode, setCryptoCurrencyCode] = useState<string | undefined>(undefined);
  const { flowType, feeAmountUSDCents } = useContext(FlowConfigContext);
  const { clientFlowType } = gknServiceData || {};

  useEffect(() => {
    if (!initialized || !gknServiceData || !initialized || !clientFlowType || !flowType || !action || !chainName) {
      return;
    }
    const currencyCode = getCurrencyCode(chainName, chainNetwork);
    setCryptoCurrencyCode(currencyCode);
    const feesConfig = loadFeeConfig({
      fee: feeAmountUSDCents,
      chainName,
      cryptoCurrencyCode: currencyCode,
      gknsData: gknServiceData,
      action,
      usdToCryptoRate: undefined,
      ownerSigns: ownerSignsEnabled,
      clientFlowType,
      flowType,
    });
    setFees(feesConfig);
  }, [
    initialized,
    requestComplete,
    feeAmountUSDCents,
    gknServiceData,
    chainName,
    chainNetwork,
    action,
    ownerSignsEnabled,
    clientFlowType,
    flowType,
    wallet,
  ]);

  useEffect(() => {
    // we don't support casper in our balance check but haven't removed official support here
    // so don't trigger the wallet balance check if the chain is casper
    if (!wallet || !initialized || !chainName || !chainNetwork || chainName === 'casper') {
      return;
    }
    const fetchWalletBalance = async () => {
      setWalletCryptoBalance({ status: 'loading' });
      return getWalletBalance(wallet, chainName as ChainType, chainNetwork)
        .then((result) => {
          if (result.error) {
            return setWalletCryptoBalance({ status: 'error', error: new Error(result.error) });
          }
          setWalletCryptoBalance({ status: 'complete', balance: result.balance });
        })
        .catch((error) => {
          Log.error('FeePanel: Error fetching wallet balance', error);
          setWalletCryptoBalance({ status: 'error', error });
        });
    };
    fetchWalletBalance();
  }, [chainName, chainNetwork, wallet, initialized]);

  useEffect(() => {
    if (
      !cryptoCurrencyCode ||
      !gknServiceData ||
      !initialized ||
      !clientFlowType ||
      !flowType ||
      !action ||
      !chainName
    ) {
      return;
    }

    const getConvertedFees = async () => {
      conversionService.getUsdConversionRate(cryptoCurrencyCode).then((result) => {
        if (result.isSuccessful()) {
          const rate = result.success();
          const feesConfig = loadFeeConfig({
            fee: feeAmountUSDCents,
            chainName,
            cryptoCurrencyCode,
            gknsData: gknServiceData,
            action,
            usdToCryptoRate: rate,
            ownerSigns: ownerSignsEnabled,
            clientFlowType,
            flowType,
          });
          setFees(feesConfig);
          return;
        }
        result.logError(`FeePanel: Error getting USD conversersion rate for ${cryptoCurrencyCode}`);
      });
    };

    getConvertedFees();
  }, [
    action,
    chainName,
    clientFlowType,
    cryptoCurrencyCode,
    feeAmountUSDCents,
    flowType,
    gknServiceData,
    initialized,
    ownerSignsEnabled,
  ]);

  useEffect(() => {
    if (!walletCryptoBalance?.balance || !fees?.crypto.total) {
      return;
    }
    const balanceDetail = getWalletBalanceDetails(walletCryptoBalance.balance, chainName as ChainType, fees);
    setWalletBalanceDetail(balanceDetail);
  }, [walletCryptoBalance, fees, chainName]);

  const value = useMemo(
    () => ({
      fees,
      walletCryptoBalance,
      walletBalanceDetail,
      cryptoCurrencyCode,
    }),
    [fees, walletCryptoBalance, walletBalanceDetail, cryptoCurrencyCode],
  );
  return <FeesContext.Provider value={value}>{children}</FeesContext.Provider>;
}
export const useFees = (): FeesOutput => useContext(FeesContext);
